/**
 * @module SortByPanel/DatasetFieldSort/utils
 * @description 字段排序：字段下拉选项 / 聚合选项 / payload 辅助（纯函数，无 JSX）
 */

import {
  FieldType,
  MetricOptions,
  MetricTypes,
  type IColumnInfo,
  type TFieldType,
} from '@dbl/core/dataSource';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';
import type { IMetricSortValueItem } from '../../../../types/settings/sortFields';

/** Select 分组：自定义字段置顶 */
export const DATASET_FIELD_CUSTOM_GROUP_KEY = '__custom__';

/**
 * 自定义字段 table_code 上传约定：空串兜底为 t0（与进 QB / 保存一致）
 */
export function normalizeDatasetFieldTableCode(tableCode?: string): string {
  return tableCode || 't0';
}

/**
 * 是否按「自定义字段」table_code 约定存储（空或 t0）
 * 表字段也可能是 t0，需结合 key / customColumns 再判断身份
 */
export function isCustomDatasetFieldTableCode(tableCode?: string): boolean {
  return !tableCode || tableCode === 't0';
}

/** 字符串 / 日期不展示的聚合（与 QB MetricsSelect DisplayValueList 一致，此处为不展示而非禁用） */
const HIDDEN_AGG_FOR_STRING_OR_DATE = new Set(['sum', 'avg']);

/**
 * tea Select 不能用空串做 option.value（「已聚合」协议值是 ''）。
 * UI 用哨兵；读写 chartSorts 时再映射回 AggregateType.Aggregated。
 */
export const DATASET_FIELD_AGGREGATED_SELECT_VALUE = '__aggregated__';

export function toAggregateSelectValue(aggregate: string | undefined): string | undefined {
  if (typeof aggregate !== 'string') {
    return undefined;
  }
  return aggregate === MetricTypes.aggregated.value
    ? DATASET_FIELD_AGGREGATED_SELECT_VALUE
    : aggregate;
}

export function fromAggregateSelectValue(selectValue: string): string {
  return selectValue === DATASET_FIELD_AGGREGATED_SELECT_VALUE
    ? MetricTypes.aggregated.value
    : selectValue;
}

export interface IDatasetFieldOptionMeta {
  value: string;
  columnName: string;
  tableCode: string;
  columnType: TFieldType | string;
  isCustom: boolean;
  /**
   * 自定义字段 SQL 已含聚合（与 CustomColumn.is_aggregate / convert 一致）。
   * true 时聚合下拉仅展示「已聚合」。
   */
  isAggregate?: boolean;
  displayName: string;
  /**
   * 搜索匹配文本（与 TableFieldList.Search 一致）：
   * - 表字段：column_display / column_name（及 en 展示名）
   * - 自定义字段：按唯一身份 name_cn 匹配（附带 name_en 仅便于搜索展示串）
   */
  searchTexts: string[];
}

/** 纯数据选项（UI text 由组件层渲染） */
export interface IDatasetFieldOptionData {
  value: string;
  groupKey: string;
  meta: IDatasetFieldOptionMeta;
}

export function buildFieldOptionValue(
  tableCode: string,
  columnName: string,
): string {
  return `${tableCode || DATASET_FIELD_CUSTOM_GROUP_KEY}::${columnName}`;
}

export function isStringOrDateFieldType(columnType: string | undefined): boolean {
  if (!columnType) {
    return false;
  }
  return (
    columnType === FieldType.String
    || columnType === FieldType.Date
    || columnType === FieldType.Time
  );
}

/**
 * 聚合下拉选项（对齐统计指标 / MetricsSelect 口径）：
 * - 自定义字段 is_aggregate：只展示「已聚合」（value=''）
 * - 否则：不展示「已聚合」；字符串/日期不展示总和、均值
 */
export function getDatasetFieldAggregateOptions(
  columnType: string | undefined,
  isEn: boolean,
  isAggregate?: boolean,
): Array<{ value: string; text: string }> {
  if (isAggregate) {
    const aggregated = MetricTypes.aggregated;
    return [{
      value: aggregated.value,
      text: (isEn ? aggregated.text_en : aggregated.text) || aggregated.value,
    }];
  }

  return MetricOptions
    .filter(opt => opt.value !== MetricTypes.aggregated.value)
    .filter(opt => {
      if (!isStringOrDateFieldType(columnType)) {
        return true;
      }
      return !HIDDEN_AGG_FOR_STRING_OR_DATE.has(opt.value);
    })
    .map(opt => ({
      value: opt.value,
      text: (isEn ? opt.text_en : opt.text) || opt.value,
    }));
}

export function pickDefaultAggregate(
  columnType: string | undefined,
  isAggregate?: boolean,
): string {
  const options = getDatasetFieldAggregateOptions(columnType, false, isAggregate);
  if (options.length > 0) {
    return options[0].value;
  }
  return MetricTypes.count.value;
}

/**
 * 字段下拉搜索：与 TableFieldList.Search 一致（toLowerCase + indexOf，不区分大小写）
 */
export function matchDatasetFieldSearchKeyword(
  searchTexts: readonly string[] | undefined,
  inputValue: string,
): boolean {
  const keyword = String(inputValue || '').toLowerCase();
  if (!keyword) {
    return true;
  }
  return (searchTexts || []).some(
    text => String(text || '').toLowerCase().indexOf(keyword) !== -1,
  );
}

/** 从 dataTables + customColumns 构建分组字段选项数据（自定义组在前） */
export function buildDatasetFieldSelectOptions(params: {
  dataTables: IGetTableInfoDto[];
  customColumns: CustomColumn[];
  isEn: boolean;
  customGroupLabel: string;
}): {
  options: IDatasetFieldOptionData[];
  groups: Record<string, string>;
  metaByValue: Record<string, IDatasetFieldOptionMeta>;
} {
  const { dataTables, customColumns, isEn, customGroupLabel } = params;
  const options: IDatasetFieldOptionData[] = [];
  const groups: Record<string, string> = {};
  const metaByValue: Record<string, IDatasetFieldOptionMeta> = {};

  if (customColumns?.length) {
    groups[DATASET_FIELD_CUSTOM_GROUP_KEY] = customGroupLabel;
    customColumns.forEach((col) => {
      // 唯一身份：name_cn（与进 QB 的 column_name 一致）；name_en 不参与逻辑
      const columnName = col.name_cn || col.id;
      if (!columnName) {
        return;
      }
      const value = buildFieldOptionValue(
        DATASET_FIELD_CUSTOM_GROUP_KEY,
        columnName,
      );
      const displayName = isEn
        ? (col.name_en || col.name_cn || columnName)
        : (col.name_cn || col.name_en || columnName);
      const meta: IDatasetFieldOptionMeta = {
        value,
        columnName,
        // 上传 / chartSorts 与进 QB 一致：自定义字段 table_code 兜底 t0
        tableCode: 't0',
        columnType: col.column_type,
        isCustom: true,
        // 与 convertCustomColumnToColumnRawInfo / MetricsSelect 一致
        isAggregate: typeof col.is_aggregate === 'boolean'
          ? col.is_aggregate
          : /\b(avg|sum|distinct|max|min|count)\s*\(/i.test(
            col.sql_expression || '',
          ),
        displayName,
        // 搜索：身份用 name_cn；name_en 仅作展示串兜底
        searchTexts: [col.name_cn, col.name_en].filter(Boolean) as string[],
      };
      metaByValue[value] = meta;
      options.push({
        value,
        groupKey: DATASET_FIELD_CUSTOM_GROUP_KEY,
        meta,
      });
    });
  }

  (dataTables || []).forEach((table, tableIndex) => {
    const tableCode = table?.table_code || `t${tableIndex}`;
    const tableLabel =
      (isEn
        ? (table?.customize_name_en || table?.customize_name || table?.table_name)
        : (table?.customize_name || table?.customize_name_en || table?.table_name))
      || tableCode;
    groups[tableCode] = String(tableLabel);

    const classLists = table?.column_class_list || [];
    if (classLists.length > 0) {
      classLists.forEach((columnClass) => {
        (columnClass?.columns || []).forEach((column: IColumnInfo) => {
          pushTableColumnOption({
            column,
            tableCode,
            options,
            metaByValue,
            isEn,
          });
        });
      });
      return;
    }

    (table?.column_list || []).forEach((col) => {
      const columnName = col.name || '';
      if (!columnName) {
        return;
      }
      pushTableColumnOption({
        column: {
          column_name: columnName,
          column_display: col.display_name || columnName,
          column_display_en: col.display_name_en || columnName,
          column_type: col.type || FieldType.String,
        },
        tableCode,
        options,
        metaByValue,
        isEn,
      });
    });
  });

  // 仅当「自定义组 + 各表组」中实际有选项的分组数 > 1 时才分组展示
  const usedGroupKeys = new Set(options.map(item => item.groupKey));
  const shouldGroup = usedGroupKeys.size > 1;
  if (!shouldGroup) {
    return {
      options: options.map(item => ({ ...item, groupKey: '' })),
      groups: {},
      metaByValue,
    };
  }

  const grouped: Record<string, string> = {};
  usedGroupKeys.forEach((key) => {
    if (groups[key]) {
      grouped[key] = groups[key];
    }
  });

  return { options, groups: grouped, metaByValue };
}

/** 构建选项所需的最小列字段（避免对不完整对象字面量 as IColumnInfo） */
interface IDatasetFieldColumnSource {
  column_name: string;
  column_display?: string;
  column_display_en?: string;
  column_type?: TFieldType | string;
}

function pushTableColumnOption(params: {
  column: IDatasetFieldColumnSource;
  tableCode: string;
  options: IDatasetFieldOptionData[];
  metaByValue: Record<string, IDatasetFieldOptionMeta>;
  isEn: boolean;
}): void {
  const { column, tableCode, options, metaByValue, isEn } = params;
  const columnName = column.column_name;
  if (!columnName) {
    return;
  }
  const value = buildFieldOptionValue(tableCode, columnName);
  if (metaByValue[value]) {
    return;
  }
  const displayName = isEn
    ? (column.column_display_en || column.column_display || columnName)
    : (column.column_display || column.column_display_en || columnName);
  const meta: IDatasetFieldOptionMeta = {
    value,
    columnName,
    tableCode,
    columnType: column.column_type,
    isCustom: false,
    displayName,
    // 与 TableFieldList 表字段搜索一致：column_display / column_name（附带 en 展示名）
    searchTexts: [
      column.column_display,
      column.column_display_en,
      column.column_name,
    ].filter(Boolean) as string[],
  };
  metaByValue[value] = meta;
  options.push({
    value,
    groupKey: tableCode,
    meta,
  });
}

export function toDatasetFieldSortItem(
  meta: IDatasetFieldOptionMeta,
  aggregate: string,
  order: 'asc' | 'desc',
): IMetricSortValueItem {
  return {
    key: meta.columnName,
    value: order,
    table_code: meta.isCustom
      ? normalizeDatasetFieldTableCode(meta.tableCode)
      : (meta.tableCode || 't0'),
    aggregate,
  };
}

/** 读取 chartSorts 项上的 aggregate；缺省时按字段 meta 回落默认值 */
export function resolveSortItemAggregate(
  item: IMetricSortValueItem | undefined,
  meta?: IDatasetFieldOptionMeta,
): string {
  if (typeof item?.aggregate === 'string') {
    return item.aggregate;
  }
  return pickDefaultAggregate(meta?.columnType, meta?.isAggregate);
}

/** 仅改升降序，保留原 key / table_code / aggregate */
export function withDatasetFieldSortOrder(
  item: IMetricSortValueItem,
  order: 'asc' | 'desc',
  meta?: IDatasetFieldOptionMeta,
): IMetricSortValueItem {
  if (meta) {
    return toDatasetFieldSortItem(
      meta,
      resolveSortItemAggregate(item, meta),
      order,
    );
  }
  return {
    key: item.key,
    table_code: item.table_code || 't0',
    value: order,
    aggregate: resolveSortItemAggregate(item),
  };
}

/** 无 meta 时按当前项改聚合 */
export function withDatasetFieldSortAggregate(
  item: IMetricSortValueItem,
  aggregate: string,
): IMetricSortValueItem {
  return {
    key: item.key,
    table_code: item.table_code || 't0',
    value: item.value === 'desc' ? 'desc' : 'asc',
    aggregate,
  };
}

/**
 * 面板受控值规范化：无有效字段时默认首项；聚合不在可选列表时回落默认聚合。
 * 无需变更时返回 undefined（避免多余 onChange）。
 */
export function normalizeDatasetFieldSortValue(params: {
  current: IMetricSortValueItem | undefined;
  fieldOptionData: IDatasetFieldOptionData[];
  metaByValue: Record<string, IDatasetFieldOptionMeta>;
}): IMetricSortValueItem[] | undefined {
  const { current, fieldOptionData, metaByValue } = params;

  if (!current?.key) {
    const first = fieldOptionData[0]?.meta;
    if (!first) {
      return undefined;
    }
    return [
      toDatasetFieldSortItem(
        first,
        pickDefaultAggregate(first.columnType, first.isAggregate),
        'asc',
      ),
    ];
  }

  const selectValue = resolveSelectValueFromSortItem(current, metaByValue);
  const selectedMeta = metaByValue[selectValue];
  if (!selectedMeta) {
    return undefined;
  }

  const allowed = getDatasetFieldAggregateOptions(
    selectedMeta.columnType,
    false,
    selectedMeta.isAggregate,
  ).map(item => item.value);
  // 「已聚合」value 为 ''，不能用 truthy 判断
  if (
    typeof current.aggregate === 'string'
    && allowed.includes(current.aggregate)
  ) {
    return undefined;
  }

  return [
    toDatasetFieldSortItem(
      selectedMeta,
      pickDefaultAggregate(selectedMeta.columnType, selectedMeta.isAggregate),
      current.value === 'desc' ? 'desc' : 'asc',
    ),
  ];
}

/**
 * 将 chartSorts 项还原为 Select value。
 * 自定义字段上传 table_code=t0：有 metaByValue 时优先命中自定义选项，避免与表 t0 撞车。
 */
export function resolveSelectValueFromSortItem(
  item: IMetricSortValueItem | undefined,
  metaByValue?: Record<string, IDatasetFieldOptionMeta>,
): string {
  if (!item?.key) {
    return '';
  }
  const customValue = buildFieldOptionValue(
    DATASET_FIELD_CUSTOM_GROUP_KEY,
    item.key,
  );
  const tableValue = buildFieldOptionValue(
    normalizeDatasetFieldTableCode(item.table_code),
    item.key,
  );

  if (metaByValue) {
    if (
      isCustomDatasetFieldTableCode(item.table_code)
      && metaByValue[customValue]
    ) {
      return customValue;
    }
    if (metaByValue[tableValue]) {
      return tableValue;
    }
    if (metaByValue[customValue]) {
      return customValue;
    }
  }

  // 无 meta：历史空 table_code 走自定义组；已有 t0 走表码（上传约定）
  if (!item.table_code) {
    return customValue;
  }
  return tableValue;
}
